import { useCallback, useEffect, useRef, useState } from "react";

import { tokenArr } from "@/collocate";
import useFiboChainRpc from "@/store/builtInRpc/useFiboChainRpc";
import { ZeroAddress } from "ethers";

import useDebounceFn from "./useDebounceFn";

// 中转 Token 列表（可扩展），用于多跳路径查找
const MID_TOKENS = [tokenArr[1].address];
const initData = {
  path: [],
  amountOut: BigInt(0),
};
/**
 * 查找最佳兑换路径 Hook（支持多跳，自动过滤无效路径）
 * @param fromTokenAddress 输入代币地址
 * @param toTokenAddress   输出代币地址
 * @param amountInRaw      输入数量（bigint）
 * @returns { bestTrade, loading }
 */
const useBestTradeExactIn = (
  fromTokenAddress: string,
  toTokenAddress: string,
  amountInRaw: bigint
) => {
  // 获取只读路由合约和工厂合约实例
  const { routerAbiReadOnly, factoryAbiReadOnly } = useFiboChainRpc();
  // 请求唯一 ID（用于竞态处理，保证只处理最后一次请求结果）
  const requestIdRef = useRef(0);
  // 最优路径及输出金额
  const [bestTrade, setBestTrade] = useState<{
    path: string[];
    amountOut: bigint;
  }>(initData);

  // loading 状态
  const [bestTradeExactInLod, setLoading] = useState(false);

  // 缓存已查询过的 pair 是否存在，避免重复请求
  const pairCacheRef = useRef(new Map<string, boolean>());

  /**
   * 判断某一 token pair 是否存在 LP
   * @param tokenA
   * @param tokenB
   * @returns boolean | undefined
   */
  const hasPair = useCallback(
    async (tokenA: string, tokenB: string) => {
      if (!factoryAbiReadOnly) return;

      const key = `${tokenA}-${tokenB}`;
      const cache = pairCacheRef.current;

      if (cache.has(key)) return cache.get(key);
      const pairAddress = await factoryAbiReadOnly.getPair(tokenA, tokenB);
      const exists = pairAddress !== ZeroAddress;
      cache.set(key, exists);
      return exists;
    },
    [factoryAbiReadOnly]
  );

  /**
   * 构建所有可能的兑换路径（from -> mid... -> to），最多 4 跳（3 个中转）
   * @param mids 中转 token 列表
   * @param maxDepth 最大跳数
   * @returns 所有路径数组
   */
  const generatePaths = useCallback(
    (mids: string[], maxDepth = 3) => {
      const results: string[][] = [];
      // 深度优先遍历所有路径
      const dfs = (path: string[], depth: number) => {
        if (depth > maxDepth) return;

        for (const mid of mids) {
          if (path.includes(mid)) continue; // 避免环路
          const newPath = [...path, mid];
          results.push([...newPath, toTokenAddress]);
          dfs(newPath, depth + 1);
        }
      };

      results.push([fromTokenAddress, toTokenAddress]); // 直接路径
      dfs([fromTokenAddress], 1);
      return results;
    },
    [fromTokenAddress, toTokenAddress]
  );

  /**
   * 查找最佳兑换路径及输出金额
   */
  const getBestTradePath = useCallback(async () => {
    if (
      !routerAbiReadOnly ||
      !factoryAbiReadOnly ||
      !fromTokenAddress ||
      !toTokenAddress
    ) {
      setBestTrade(initData);
      return;
    }

    const currentRequestId = ++requestIdRef.current;
    setLoading(true);

    try {
      // 生成所有可能路径
      const allPaths = generatePaths(MID_TOKENS);
      const validPaths: string[][] = [];

      // 过滤掉不存在 LP 的路径
      for (const path of allPaths) {
        let valid = true;
        for (let i = 0; i < path.length - 1; i++) {
          const has = await hasPair(path[i], path[i + 1]);
          if (!has) {
            valid = false;
            break;
          }
        }
        if (valid) validPaths.push(path);
      }

      // 如果输入为 0，仅返回第一条有效路径（不查 amountOut）
      if (amountInRaw === BigInt(0)) {
        if (validPaths.length === 0) {
          setBestTrade(initData);
        } else {
          // 检查请求 ID 是否仍然是最新的
          if (currentRequestId === requestIdRef.current) {
            setBestTrade({
              path: validPaths[0],
              amountOut: BigInt(0),
            });
          }
        }
        return;
      }

      // 查询所有有效路径的输出金额
      const results: {
        path: string[];
        amountOut: bigint;
      }[] = [];

      for (const path of validPaths) {
        try {
          const amountsOut: bigint[] = await routerAbiReadOnly.getAmountsOut(
            amountInRaw,
            path
          );
          results.push({
            path,
            amountOut: amountsOut[amountsOut.length - 1],
          });
        } catch (e) {
          // 忽略失败路径
        }
      }

      // 只处理最后一次请求的结果
      if (currentRequestId !== requestIdRef.current) return;

      if (results.length === 0) {
        setBestTrade(initData);
        return;
      }

      // 选择输出最多的路径作为最佳路径
      const best = results.reduce((a, b) =>
        b.amountOut > a.amountOut ? b : a
      );
      setBestTrade(best);
    } catch (e) {
      console.error("查找最优路径失败:", e);
      if (currentRequestId === requestIdRef.current) {
        setBestTrade(initData);
      }
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [
    amountInRaw,
    factoryAbiReadOnly,
    fromTokenAddress,
    generatePaths,
    hasPair,
    routerAbiReadOnly,
    toTokenAddress,
  ]);

  // 防抖处理，避免频繁请求
  const getBestTradePathDebounceFns = useDebounceFn(getBestTradePath, 500);
  // 依赖变化时自动查找最优路径
  useEffect(() => {
    getBestTradePathDebounceFns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getBestTradePath]);

  return {
    bestTrade, // 最优路径及输出金额
    bestTradeExactInLod, // 是否加载中
    getBestTradePath,
  };
};

export default useBestTradeExactIn;
